import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Paper, Stack, Grid, CircularProgress, Card
} from '@mui/material';
import {
  collection, query, where, getDocs, addDoc,
  updateDoc, doc, deleteDoc, getDoc
} from 'firebase/firestore';
import { auth, db } from '../../../firebase';
import { toast } from 'react-toastify';
import TablePagination from '@mui/material/TablePagination';

const CasteCat = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ type: '', description: '' });
  const [loading, setLoading] = useState(false);
  const [castes, setCastes] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [schoolId, setSchoolId] = useState(null);

  // Fetch the current user's schoolId
  const fetchSchoolId = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser?.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setSchoolId(data.schoolId);
      } else {
        toast.error("User not found");
      }
    } catch (err) {
      console.error("Error fetching user:", err);
      toast.error("Error fetching user");
    }
  };

  // Fetch castes for this school
  const fetchCastes = async (schoolId) => {
    if (!schoolId) return;
    try {
      const q = query(collection(db, 'castes'), where('schoolId', '==', schoolId));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCastes(data);
    } catch (error) {
      console.error("Error fetching castes:", error);
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchSchoolId();
    };
    init();
  }, []);

  // Fetch castes whenever schoolId changes
  useEffect(() => {
    if (schoolId) fetchCastes(schoolId);
  }, [schoolId]);

  const handleOpen = () => {
    setFormData({ type: '', description: '' });
    setEditMode(false);
    setEditId(null);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    const { type, description } = formData;
    if (!type) {
      toast.error("All fields are required");
      return;
    }

    if (!schoolId) {
      toast.error("School ID not found");
      return;
    }

    setLoading(true);

    try {
      if (editMode && editId) {
        await updateDoc(doc(db, 'castes', editId), {
          type,
          description
        });
        toast.success("Caste updated");
      } else {
        await addDoc(collection(db, 'castes'), {
          type,
          description,
          schoolId
        });
        toast.success("Caste added");
      }

      fetchCastes(schoolId);
      handleClose();
    } catch (err) {
      console.error(err);
      toast.error("Error: " + err.message);
    }

    setLoading(false);
  };

  const handleEdit = (cat) => {
    setFormData({ type: cat.type, description: cat.description });
    setEditId(cat.id);
    setEditMode(true);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, 'castes', id));
      toast.success("Caste deleted");
      fetchCastes(schoolId);
    } catch (err) {
      console.error(err);
      toast.error("Error deleting category");
    }
  };

  const paginated = castes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button variant="contained" onClick={handleOpen}>Add Caste</Button>
        <Typography variant="h5" fontWeight="bold">Castes</Typography>
      </Stack>

      <Card>
        <Paper>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#e0e0e0' }}>
                <TableCell>#</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">No Castes found</TableCell>
                </TableRow>
              ) : (
                paginated.map((cat, index) => (
                  <TableRow key={cat.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleEdit(cat)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleDelete(cat.id)}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                    <TableCell>{cat.type}</TableCell>
                    <TableCell>{cat.description}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 20, 30]}
            component="div"
            count={castes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>{editMode ? 'Edit' : 'Add'} Caste</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                fullWidth
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                size="small"
                multiline
                rows={3}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="error">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (editMode ? 'Update' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CasteCat;